import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useLocation } from 'react-router';

import { getFocusable, handleTabTrap } from './focus-trap';
import { SidebarContent } from './SidebarContent';

/** Navegación primaria en móvil: botón hamburguesa + drawer off-canvas con el
 *  sidebar completo. Oculto en ≥900 (CSS). Accesible (UX-A11Y-01): role=dialog,
 *  focus trap, Escape, retorno de foco al gatillo y cierre al cambiar de ruta. */
export function MobileNav(): ReactElement {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Cerrar al navegar (evita dejar el drawer abierto sobre otra pantalla).
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Cerrar al pasar a escritorio: ahí el sidebar es permanente y el drawer se
  // oculta por CSS; sin esto quedaría el scroll bloqueado y el trap activo.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 900px)');
    const onChange = (e: MediaQueryListEvent): void => {
      if (e.matches) {
        setOpen(false);
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Mientras está abierto: foco inicial en el panel, focus trap + Escape,
  // bloqueo del scroll de fondo y retorno de foco al gatillo al cerrar.
  useEffect(() => {
    if (!open) {
      return;
    }
    const trigger = triggerRef.current;
    getFocusable(panelRef.current ?? document.body)[0]?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      handleTabTrap(e, panelRef.current);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      trigger?.focus();
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="iconbtn hamburger"
        aria-label="Abrir menú de navegación"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          aria-hidden="true"
        >
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      {open && (
        <>
          <div className="drawer-overlay" aria-hidden="true" onClick={() => setOpen(false)} />
          <div
            ref={panelRef}
            className="drawer-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Navegación"
          >
            <div className="drawer-head">
              <button
                type="button"
                className="iconbtn"
                aria-label="Cerrar menú"
                onClick={() => setOpen(false)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            <SidebarContent onNavigate={() => setOpen(false)} />
          </div>
        </>
      )}
    </>
  );
}
